<template>
  <div class="MultiselectInput">
    <div
      v-if="inputHas.label"
      class="MultiselectInput-label p p-bold app-color-1 capitalize"
      :class="{
        required: required,
      }"
    >
      {{ label }}
    </div>
    <v-combobox
      v-model="selected"
      :label="label"
      :rules="rules"
      :hide-details="hideDetails"
      single-line
      :placeholder="inputHas.placeholder ? placeholder : inputHas.label ? label : '...'"
      color="#004f9f"
      :allow-overflow="false"
      outlined
      chips
      attach
      :multiple="multiple"
      :hint="hint"
      persistent-hint
      :items="options"
      :item-text="itemText"
      :item-value="itemValue"
      :prepend-inner-icon="inputHas.icon ? icon : null"
      @change="handleUpdate"
    >
      <template v-slot:selection="data">
        <v-chip
          :key="data.item[itemValue]"
          v-bind="data.attrs"
          :input-value="data.selected"
          :disabled="data.disabled"
          :class="{ 'additional-results': data.index === 1 }"
        >
          <span v-if="data.index === 0">
            {{ data.item[itemText] }}
            <v-icon
              v-if="multiple"
              size="16"
              color="#004F9F"
              class="ml-1"
              @click.stop.prevent="removeItem(data.index)"
            >close</v-icon>
          </span>
          <span v-if="data.index === 1"> +{{ additionalSelected }} </span>
        </v-chip>
      </template>
    </v-combobox>
  </div>
</template>

<script type="text/javascript">
  import mixinUi from '@/mixins/utils/ui.js'

  export default {
    mixins: [mixinUi,],
    props: {
      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      default: {
        validator: (v) => true,
        default: null,
      },
      icon: {
        type: String,
        default: '',
      },
      itemText: {
        type: String,
        default: '',
      },
      itemValue: {
        type: String,
        default: '',
      },
      options: {
        type: Array,
        required: true,
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      rules: {
        type: Array,
        default: () => [],
      },
      required: {
        type: Boolean,
        default: false,
      },
      hint: {
        type: String,
        default: '',
      },
      hideDetails: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        selected: [],
      }
    },
    computed: {
      inputHas() {
        const out = {
          label: this.label !== '',
          placeholder: this.placeholder !== '',
          icon: this.icon !== '',
          default: this.default !== null && this.default !== '',
        }

        return out
      },
      countSelected() {
        return this.selected.length
      },
      additionalSelected() {
        return this.countSelected - 1
      },
    },
    watch: {
      default: function(val) {
        this.setFromValue(val)
      },
    },
    created() {
      if (this.inputHas.default) {
        this.setFromValue(this.default)
      } else {
        if (!this.multiple) {
          this.setFromValue(null)
        }
      }
    },
    methods: {
      removeItem(index) {
        if (this.multiple) {
          this.selected.splice(index, 1)
          this.handleUpdate()
        } else {
          this.selected = {}
          this.handleUpdate()
        }
      },

      setFromValue(value) {
        if (this.multiple) {
          this.selected = []
          for (const v in value) {
            for (const i in this.options) {
              if (this.options[i][this.itemValue] === value[v]) {
                const obj = {}
                obj[this.itemValue] = value[v]
                obj[this.itemText] = this.options[i][this.itemText]
                this.selected.push(obj)
              }
            }
          }
        } else {
          this.selected = {}
          if (value === []) {
            this.selected = ''

            return
          }
          const obj = this.options.find((item) => {
            // eslint-disable-next-line eqeqeq
            return item.code == value
          })
          this.selected = obj
        }
      },

      handleUpdate() {
        const out = []
        if (this.multiple) {
          for (const i in this.selected) {
            out.push(this.selected[i][this.itemValue])
          }
          this.$emit('input', out)
        } else {
          const update = (this.selected && this.selected[this.itemValue]) || null
          this.$emit('input', update)
        }
      },
    },
  }
</script>

<style lang="scss">
/* stylelint-disable max-nesting-depth */
.MultiselectInput {
  .MultiselectInput-label {
    margin-left: 8px;
    margin-bottom: 4px;
    &.required::after {
      content: '*';
    }
  }

  .v-input.v-autocomplete {
    .v-input__control {
      .v-input__slot {
        .v-input__prepend-inner {
          margin-top: 0;
          height: 48px;
          position: relative;
          left: -11px;
          width: 48px;
          margin-right: -11px;
          border: 1.4px solid $color-d9dfff;
          border-radius: 8px 0 0 8px;
          background-color: $color-217_223_255_64;
          text-align: center;
          padding-right: 0;
          padding-top: 12px;

          .v-input__icon {
            i {
              font-size: 20px;
              color: $color-009ee0 !important;
            }
          }
        }

        fieldset {
          max-height: 53px;
          border: 1.4px solid $color-d9dfff;
          border-radius: 8px;
        }

        .v-select__slot {
          max-height: 48px;
          padding-left: 8px;
          label {
            left: 5px !important;
            right: auto !important;
            top: auto !important;
          }
          .v-select__selections {
            .v-chip {
              border-radius: 4px;
              text-transform: uppercase;
              font-size: 12px;
              letter-spacing: 0.32px;
              line-height: 20px;

              &:not(.additional-results) {
                font-weight: 600;
                background-color: $color-f3f9ff;
                color: $color-004f9f;
              }

              &.additional-results {
                background-color: $color-009ee0;
                color: $color-ffffff;
              }
            }

            & > .v-chip:nth-child(n + 3) {
              display: none;
            }
          }
          .v-input__append-inner {
            margin-top: 13px;
          }
        }
      }
    }
  }
  .v-input.error--text {
    .v-input__control {
      .v-input__slot {
        fieldset {
          border: 1.4px solid $color-ff524f;
        }
      }
    }
  }
}
</style>
